/**
 * V5 — Regroupement des murs par famille pour l'UI.
 *
 * Le catalogue materials.walls contient 71+ entrées rangées par famille
 * (pierre, pisé, blocs béton, brique, monomur, béton cellulaire…). Le
 * moteur 3CL utilise la clé technique brute (catalog.walls) ; ce mapping
 * est purement présentationnel pour ranger les options dans des
 * <optgroup> lisibles.
 *
 * Catégories alignées sur les entrées de materials_catalog.yaml § Murs.
 * Le matching est par préfixe de clé pour absorber automatiquement les
 * alias rétro-compatibles (pierre_remplissage, bloc_beton_creux, etc.)
 * qui commencent comme une entrée standard.
 */

export interface WallCategory {
  match: (key: string) => boolean;
  label: string;
}

export const WALL_CATEGORIES: WallCategory[] = [
  { match: (k) => k.startsWith("pierre_"), label: "Pierre de taille / moellons" },
  { match: (k) => k.startsWith("pise_"), label: "Pisé / terre crue" },
  { match: (k) => k.startsWith("pan_de_bois"), label: "Pan de bois" },
  { match: (k) => k.startsWith("rondins_"), label: "Bois rondins" },
  { match: (k) => k === "ossature_bois", label: "Ossature bois" },
  {
    match: (k) => k.startsWith("brique_pleine_simple_"),
    label: "Brique pleine simple",
  },
  {
    match: (k) => k.startsWith("brique_pleine_double_"),
    label: "Brique pleine double (lame d'air)",
  },
  { match: (k) => k.startsWith("brique_creuse_"), label: "Brique creuse" },
  {
    match: (k) => k.startsWith("bloc_beton_plein_"),
    label: "Blocs béton pleins (parpaings pleins)",
  },
  {
    match: (k) => k.startsWith("bloc_beton_creux_"),
    label: "Blocs béton creux (parpaings creux)",
  },
  {
    match: (k) => k.startsWith("beton_banche_"),
    label: "Béton banché (coffrage perdu coulé)",
  },
  { match: (k) => k.startsWith("machefer_"), label: "Béton de mâchefer" },
  { match: (k) => k.startsWith("monomur_"), label: "Monomur terre cuite" },
  {
    match: (k) => k.startsWith("beton_cellulaire_"),
    label: "Béton cellulaire (Siporex)",
  },
];

/** Regroupe une liste d'options de murs par catégorie pour l'affichage. */
export function groupWallOptions(
  walls: ReadonlyArray<{ key: string; label?: string }>,
): Array<{ label: string; items: Array<{ key: string; label?: string }> }> {
  const buckets = new Map<string, Array<{ key: string; label?: string }>>();
  for (const cat of WALL_CATEGORIES) buckets.set(cat.label, []);
  const autres: Array<{ key: string; label?: string }> = [];
  for (const w of walls) {
    const cat = WALL_CATEGORIES.find((c) => c.match(w.key));
    if (cat) {
      buckets.get(cat.label)!.push(w);
    } else {
      autres.push(w);
    }
  }
  const out: Array<{ label: string; items: Array<{ key: string; label?: string }> }> = [];
  for (const cat of WALL_CATEGORIES) {
    const items = buckets.get(cat.label) ?? [];
    if (items.length > 0) out.push({ label: cat.label, items });
  }
  if (autres.length > 0) out.push({ label: "Autres / alias", items: autres });
  return out;
}
